import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  FileUp,
  Loader2,
  MoreVertical,
  Plus,
  Upload,
  UploadCloud,
} from 'lucide-react'
import { useTenant } from '@/providers/TenantProvider'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { FEATURES } from '@/lib/featureFlags'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import {
  useCreateCatalogEntry,
  useImportCatalogEntries,
  useProductCatalog,
  useProductCatalogFacets,
  useStorageTempMappings,
  useUpdateCatalogStatus,
} from '@/hooks/useProducts'
import { productsApi, type CreateCatalogEntryPayload } from '@/api/productsApi'
import {
  CATALOG_IMPORT_HEADERS,
  parseProductCatalogCsv,
  parseProductCatalogWorkbook,
} from '@/lib/productsExcel'
import ExcelJS from 'exceljs'
import { catalogExportFilename, catalogToCsv } from '@/lib/catalogExport'
import {
  ISBT_LABELS,
  SUMMARY_COLUMN_KEYS,
  TEMPLATE_EXAMPLE_ROW,
  formatIsbtValue,
} from '@/lib/isbtCatalog'
import { CatalogFilterBar } from '@/components/products/CatalogFilterBar'
import { ProductCodeDialog } from '@/components/products/ProductCodeDialog'
import {
  EditCatalogStatusDialog,
  SetAllCatalogStatusDialog,
  UploadCatalogStatusDialog,
} from '@/components/products/CatalogStatusDialogs'
import { StorageTempMappingDialog } from '@/components/products/StorageTempMappingDialog'
import { statusLabel } from '@/lib/productCodeStatus'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import type { ProductCatalogEntry, ProductCatalogFilters, ProductCatalogRow } from '@/types/products'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * Configure → Products: the ISBT 128 product-code list the "Create Product"
 * picker draws from. A separate catalog from Inventory's Material master data —
 * see src/types/products.ts.
 *
 * GLOBAL reference data: the standard's 14,911 codes, shared by every tenant,
 * which is why this screen reads one page at a time and filters server-side
 * instead of holding the table in memory.
 *
 * The table shows eleven of the 44 columns. Everything else is behind the
 * Product Code link and present in the export — a summary, not the record.
 *
 * Active and Note (z8nrz7e72t) are the exception to "shared by every tenant":
 * they are the SELECTED tenant's own, and the only two things on this screen a
 * user changes about an existing code — one row at a time, by selection, all at
 * once, or by uploading an edited Export. The Create Product picker offers
 * Active codes only.
 */

const PRODUCTS_TABLE_ID = 'product-catalog-settings'
const PAGE_SIZE = 50

// Built from the same key list the cells render from, so a column can never
// appear in one and not the other. Product Code is locked: it identifies the
// row and it is the link into the full record.
//
// Every column sorts, SERVER-side (z8nrz7e4h6): the catalogue is read a page at
// a time, and each key is one of the backend's allowed sorts (CATALOG_SORTS in
// products.service).
//
// Select, Status and Note are the tenant's (z8nrz7e72t). They do not sort: the
// status is resolved per tenant, and the backend's sorts are catalog columns.
const [CODE_KEY, ...OTHER_SUMMARY_KEYS] = SUMMARY_COLUMN_KEYS
const isbtColumn = (key: (typeof SUMMARY_COLUMN_KEYS)[number], locked = false): TableColumnDef => ({
  id: key,
  header: ISBT_LABELS[key],
  locked,
  sortable: true,
})
const PRODUCTS_COLUMNS: TableColumnDef[] = [
  { id: 'select', header: 'Select', locked: true },
  isbtColumn(CODE_KEY, true),
  { id: 'active', header: 'Status' },
  ...OTHER_SUMMARY_KEYS.map(key => isbtColumn(key)),
  { id: 'note', header: 'Note' },
  // The row's three-dots menu (z8nrz7e72t). Locked: hiding it would leave a
  // status screen with no way to act on a row.
  { id: 'actions', header: 'Actions', locked: true },
]

/** The description runs to 195 characters; everything else is short. */
const HEAD_CLASSES: Record<string, string> = {
  select: 'w-8',
  description: 'min-w-[22rem]',
  note: 'min-w-[14rem]',
  actions: 'w-16',
}

export function ProductsSettings() {
  const cols = useTableColumns(PRODUCTS_TABLE_ID, PRODUCTS_COLUMNS)
  const { selectedTenantId } = useTenant()

  const [filters, setFilters] = useState<ProductCatalogFilters>({ page: 1, pageSize: PAGE_SIZE })
  // Typed text is separate from the filter that drives the query so each
  // keystroke re-renders the input without firing a request.
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState<ProductCatalogEntry | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const { can } = usePermission()
  const canManageStatus = can(PERMISSIONS.productsManage)
  const canViewMapping = can(PERMISSIONS.productsView)
  const [mappingOpen, setMappingOpen] = useState(false)
  const navigate = useNavigate()
  // The mapping lives on the Temperatures screen, an Inventory page; without
  // Inventory it still opens here, as a dialog.
  const canOpenTemperatures = useFeatureFlag(FEATURES.inventory) && can(PERMISSIONS.inventoryView)
  const openMapping = () =>
    canOpenTemperatures ? navigate(storageTemperaturesHref('mapping')) : setMappingOpen(true)
  const unmappedTemps = useStorageTempMappings().data?.unmapped ?? 0
  // Ids, not rows: a selection survives paging, and the rows on screen change.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // `flip`: opened from the row menu's "Change status", with Active pre-flipped.
  const [editing, setEditing] = useState<{ entry: ProductCatalogRow; flip: boolean } | null>(null)
  const [setAllTo, setSetAllTo] = useState<boolean | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const updateStatus = useUpdateCatalogStatus()

  // A different filter is a different set of rows, and a selection made under
  // the old one would act on codes no longer on screen.
  const { page: _page, ...filterIdentity } = filters
  const filterKey = JSON.stringify(filterIdentity)
  const [selectionFilterKey, setSelectionFilterKey] = useState(filterKey)
  if (selectionFilterKey !== filterKey) {
    setSelectionFilterKey(filterKey)
    setSelectedIds(new Set())
  }

  useEffect(() => {
    const id = setTimeout(
      () => setFilters(prev => ({ ...prev, search: searchText || undefined, page: 1 })),
      300,
    )
    return () => clearTimeout(id)
  }, [searchText])

  // A new sort is a new ordering of the whole catalogue, so page 1 again
  // (z8nrz7e4h6). Kept out of `filters` so the facets query does not refetch
  // on a sort change — the facet options do not depend on order.
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setFilters(prev => ({ ...prev, page: 1 }))
  }

  const { data, isLoading, isError, error, isFetching } = useProductCatalog({ ...filters, sort })
  const { data: facets } = useProductCatalogFacets(filters)
  const createEntry = useCreateCatalogEntry()

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const page = data?.page ?? 1
  const pageCount = Math.max(1, Math.ceil(total / (data?.pageSize || PAGE_SIZE)))
  const pageSelected = items.length > 0 && items.every(e => selectedIds.has(e.id))
  const filtered = Object.entries(filterIdentity).some(
    ([key, value]) => key !== 'pageSize' && value !== undefined && value !== '' && value !== false,
  )

  function toggleRow(id: string, on: boolean) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }

  function togglePage(on: boolean) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      for (const e of items) {
        if (on) next.add(e.id)
        else next.delete(e.id)
      }
      return next
    })
  }

  function setSelectedStatus(active: boolean) {
    updateStatus.mutate(
      { catalogEntryIds: [...selectedIds], active },
      { onSuccess: () => setSelectedIds(new Set()) },
    )
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-base font-semibold text-foreground'>ISBT 128 product codes</h2>
          <p className='text-sm text-muted-foreground'>
            The product-code list offered when creating a product from a patient form. The codes
            are shared by every tenant — the published standard. Active and Note are this
            tenant's own: only Active codes can be used for a new product.
          </p>
        </div>
        <div className='flex shrink-0 gap-2'>
          <ExportButton filters={filters} total={total} tenantId={selectedTenantId} />
          {canManageStatus && (
            <Button size='sm' variant='outline' onClick={() => setUploadOpen(true)}>
              <FileUp className='mr-1.5 h-3.5 w-3.5' />
              Upload Active / Note
            </Button>
          )}
          <Button size='sm' variant='outline' onClick={() => setImportOpen(true)}>
            <Upload className='mr-1.5 h-3.5 w-3.5' />
            Import
          </Button>
          <Button size='sm' onClick={() => setAddOpen(true)}>
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add product
          </Button>
        </div>
      </div>

      <CatalogFilterBar
        filters={filters}
        facets={facets}
        onChange={setFilters}
        searchValue={searchText}
        onSearchChange={setSearchText}
      />

      {(canManageStatus || canViewMapping) && selectedTenantId && (
        <div className='flex flex-wrap items-center gap-2 text-sm'>
          {!canManageStatus ? null : selectedIds.size > 0 ? (
            <>
              <span className='font-medium'>{selectedIds.size.toLocaleString()} selected</span>
              <Button
                size='sm'
                variant='outline'
                className='h-8'
                disabled={updateStatus.isPending}
                onClick={() => setSelectedStatus(true)}
              >
                Activate
              </Button>
              <Button
                size='sm'
                variant='outline'
                className='h-8'
                disabled={updateStatus.isPending}
                onClick={() => setSelectedStatus(false)}
              >
                Inactivate
              </Button>
              <Button size='sm' variant='ghost' className='h-8' onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
            </>
          ) : (
            <span className='text-muted-foreground'>Select codes to activate or inactivate them.</span>
          )}
          <div className='ml-auto flex gap-2'>
            {canViewMapping && (
              <Button
                size='sm'
                className='h-8'
                onClick={openMapping}
                aria-label={
                  unmappedTemps > 0
                    ? `Storage Temp Mapping, ${unmappedTemps} unmapped`
                    : 'Storage Temp Mapping'
                }
              >
                Storage Temp Mapping
                {unmappedTemps > 0 && (
                  <span className='ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-5 text-white'>
                    {unmappedTemps}
                  </span>
                )}
              </Button>
            )}
            {canManageStatus && (
              <>
                <Button
                  size='sm'
                  variant='outline'
                  className='h-8'
                  disabled={total === 0}
                  onClick={() => setSetAllTo(true)}
                >
                  Activate all
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  className='h-8'
                  disabled={total === 0}
                  onClick={() => setSetAllTo(false)}
                >
                  Inactivate all
                </Button>
              </>
            )}
          </div>
          {updateStatus.error instanceof Error && (
            <p className='w-full text-destructive'>{updateStatus.error.message}</p>
          )}
        </div>
      )}

      <Card>
        <CardContent className='p-0'>
          {!selectedTenantId && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              Select a tenant to view the product codes.
            </div>
          )}

          {selectedTenantId && isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {selectedTenantId && isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load products. {(error as Error)?.message}
            </div>
          )}

          {selectedTenantId && !isLoading && !isError && items.length === 0 && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              No product codes match those filters.
            </div>
          )}

          {selectedTenantId && !isLoading && !isError && items.length > 0 && (
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    {cols.visible.map(({ def }) => (
                      <ResizableTableHead
                        key={def.id}
                        columnId={def.id}
                        columns={cols}
                        label={def.header}
                        className={HEAD_CLASSES[def.id]}
                        resizable={def.id !== 'select' && def.id !== 'actions'}
                      >
                        {def.id === 'select' ? (
                          canManageStatus && (
                            <Checkbox
                              checked={pageSelected}
                              onCheckedChange={v => togglePage(v === true)}
                              aria-label='Select this page'
                            />
                          )
                        ) : (
                          def.header
                        )}
                      </ResizableTableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(entry => (
                    <TableRow key={entry.id} className={cn(!entry.active && 'text-muted-foreground')}>
                      <ColumnCells
                        columns={cols}
                        cells={{
                          select: (
                            <TableCell>
                              {canManageStatus && (
                                <Checkbox
                                  checked={selectedIds.has(entry.id)}
                                  onCheckedChange={v => toggleRow(entry.id, v === true)}
                                  aria-label={`Select ${entry.productDescriptionCode}`}
                                />
                              )}
                            </TableCell>
                          ),
                          active: (
                            <TableCell>
                              <StatusBadge
                                entry={entry}
                                onEdit={canManageStatus ? () => setEditing({ entry, flip: false }) : undefined}
                              />
                            </TableCell>
                          ),
                          note: (
                            <TableCell className='max-w-[18rem] truncate text-xs' title={entry.note || undefined}>
                              {entry.note || '—'}
                            </TableCell>
                          ),
                          actions: (
                            <TableCell>
                              {canManageStatus && (
                                <RowActionsMenu
                                  entry={entry}
                                  onChangeStatus={() => setEditing({ entry, flip: true })}
                                />
                              )}
                            </TableCell>
                          ),
                          ...Object.fromEntries(
                            SUMMARY_COLUMN_KEYS.map(key => [
                              key,
                              <TableCell
                                key={key}
                                className={
                                  key === 'description'
                                    ? 'max-w-[28rem] truncate font-mono text-xs text-muted-foreground'
                                    : undefined
                                }
                                // The full HL7 string on hover: the cell truncates
                                // it, and the popup is a click away.
                                title={key === 'description' ? entry.description : undefined}
                              >
                                {key === 'productDescriptionCode' ? (
                                  <button
                                    type='button'
                                    onClick={() => setSelected(entry)}
                                    className='font-mono font-medium text-primary hover:underline'
                                  >
                                    {entry.productDescriptionCode}
                                  </button>
                                ) : (
                                  formatIsbtValue(entry, key)
                                )}
                              </TableCell>,
                            ]),
                          ),
                        }}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shown at zero too. A filter that matches nothing is a result, and
          dropping the whole line left the reader unsure whether the filter had
          applied at all. */}
      {selectedTenantId && !isError && !isLoading && (
        <div className='flex items-center justify-between text-sm text-muted-foreground'>
          <span>
            {total.toLocaleString()} code{total === 1 ? '' : 's'}
            {isFetching && ' · updating…'}
          </span>
          <div className='flex items-center gap-2'>
            <Button
              size='sm'
              variant='outline'
              disabled={page <= 1}
              onClick={() => setFilters(prev => ({ ...prev, page: page - 1 }))}
            >
              <ChevronLeft className='h-3.5 w-3.5' />
            </Button>
            <span className='tabular-nums'>
              Page {page.toLocaleString()} of {pageCount.toLocaleString()}
            </span>
            <Button
              size='sm'
              variant='outline'
              disabled={page >= pageCount}
              onClick={() => setFilters(prev => ({ ...prev, page: page + 1 }))}
            >
              <ChevronRight className='h-3.5 w-3.5' />
            </Button>
          </div>
        </div>
      )}

      <ProductCodeDialog entry={selected} onClose={() => setSelected(null)} />
      <EditCatalogStatusDialog
        entry={editing?.entry ?? null}
        flip={editing?.flip}
        onClose={() => setEditing(null)}
      />
      <SetAllCatalogStatusDialog
        active={setAllTo}
        filters={filters}
        total={total}
        filtered={filtered}
        onClose={() => setSetAllTo(null)}
      />
      <UploadCatalogStatusDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <StorageTempMappingDialog open={mappingOpen} onClose={() => setMappingOpen(false)} />
      <AddProductDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={payload => createEntry.mutate(payload, { onSuccess: () => setAddOpen(false) })}
        saving={createEntry.isPending}
        error={createEntry.error instanceof Error ? createEntry.error.message : null}
      />
      <ImportProductsDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  )
}

/**
 * The row's three-dots menu. Change status opens the Active / Note dialog with
 * Active already flipped, so the user confirms the change and is asked for the
 * note in the same step.
 */
function RowActionsMenu({
  entry,
  onChangeStatus,
}: {
  entry: ProductCatalogRow
  onChangeStatus: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='h-8 w-8 p-0'
          aria-label={`Actions for ${entry.productDescriptionCode}`}
          title='Actions'
        >
          <MoreVertical className='h-4 w-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-44 p-1'>
        <button
          type='button'
          title={entry.active ? 'Make this code Inactive' : 'Make this code Active'}
          onClick={() => {
            setOpen(false)
            onChangeStatus()
          }}
          className='flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent'
        >
          Change status
        </button>
      </PopoverContent>
    </Popover>
  )
}

/**
 * The Status cell: a badge, and the way into editing Active and Note when the
 * user may. Inactive shows its note on hover — the first question about a
 * retired code is why.
 */
function StatusBadge({ entry, onEdit }: { entry: ProductCatalogRow; onEdit?: () => void }) {
  const badge = (
    <Badge variant={entry.active ? 'default' : 'secondary'} className='whitespace-nowrap'>
      {statusLabel(entry.active)}
    </Badge>
  )
  if (!onEdit) return <span title={entry.note || undefined}>{badge}</span>
  return (
    <button
      type='button'
      onClick={onEdit}
      title={entry.note ? `${entry.note} — click to edit` : 'Edit Active / Note'}
      className='rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
    >
      {badge}
    </button>
  )
}

/**
 * Export every row matching the current filters — all 44 fields, not the eleven
 * on screen.
 *
 * Pages through the API rather than exporting what is rendered: the table shows
 * 50 of 14,911, and an export of the visible page would be a quietly wrong file.
 */
function ExportButton({
  filters,
  total,
  tenantId,
}: {
  filters: ProductCatalogFilters
  total: number
  tenantId: string | null
}) {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(0)

  async function handleExport() {
    if (!tenantId) return
    setBusy(true)
    setDone(0)
    try {
      const all: ProductCatalogRow[] = []
      // 1,000 is the server's ceiling — 15 round trips for the whole catalogue.
      const pageSize = 1000
      for (let page = 1; ; page += 1) {
        const result = await productsApi.listCatalog(tenantId, { ...filters, page, pageSize })
        all.push(...result.items)
        setDone(all.length)
        if (all.length >= result.total || result.items.length === 0) break
      }

      // With this tenant's Active and Note last, so the file can be edited and
      // sent back through "Upload Active / Note" (z8nrz7e72t).
      const blob = new Blob([catalogToCsv(all, { withStatus: true })], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = catalogExportFilename()
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button size='sm' variant='outline' onClick={handleExport} disabled={busy || total === 0}>
      <Download className='mr-1.5 h-3.5 w-3.5' />
      {busy ? `Exporting ${done.toLocaleString()}/${total.toLocaleString()}…` : 'Export'}
    </Button>
  )
}

/**
 * Add one code by hand.
 *
 * Only the fields a person can reasonably type: the code, the raw description,
 * and the columns the table shows. The other 33 parsed columns come from the
 * importer, and a 44-field form would be a worse way to enter any of them.
 */
const MANUAL_FIELDS = SUMMARY_COLUMN_KEYS

const EMPTY_ENTRY = {
  productDescriptionCode: '',
  description: '',
  productClass: '',
  sourceTissue: '',
  anticoagulant: '',
  storageTemperature: '',
  cryoprotectant: '',
  preservationState: '',
  thirdPartyComponents: '',
  geneticallyModified: '',
  processing: '',
} satisfies Partial<CreateCatalogEntryPayload>

type ManualEntry = typeof EMPTY_ENTRY

function AddProductDialog({
  open,
  onClose,
  onSave,
  saving,
  error,
}: {
  open: boolean
  onClose: () => void
  onSave: (payload: CreateCatalogEntryPayload) => void
  saving: boolean
  error: string | null
}) {
  const [entry, setEntry] = useState<ManualEntry>(EMPTY_ENTRY)

  function handleOpenChange(next: boolean) {
    if (!next) {
      setEntry(EMPTY_ENTRY)
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Add product code</DialogTitle>
          <DialogDescription>
            Add one code to the ISBT 128 list. The remaining parsed columns are left blank — use
            Import to load a parsed file.
          </DialogDescription>
        </DialogHeader>

        <div className='grid max-h-[55vh] grid-cols-2 gap-3 overflow-y-auto'>
          {MANUAL_FIELDS.map(key => (
            <div key={key} className={key === 'description' ? 'col-span-2 space-y-1' : 'space-y-1'}>
              <label className='text-xs font-medium text-muted-foreground'>
                {ISBT_LABELS[key]}
              </label>
              <Input
                value={String(entry[key as keyof ManualEntry] ?? '')}
                onChange={e => setEntry(prev => ({ ...prev, [key]: e.target.value }))}
                className='h-9'
              />
            </div>
          ))}
        </div>

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(entry as CreateCatalogEntryPayload)}
            disabled={saving || !entry.productDescriptionCode.trim()}
          >
            {saving ? 'Adding…' : 'Add product code'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Bulk import — see src/lib/productsExcel.ts for the parser (pure, unit-tested). */
function ImportProductsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const importEntries = useImportCatalogEntries()
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [buildingTemplate, setBuildingTemplate] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const result = importEntries.data
  const { progress } = importEntries
  const busy = parsing || importEntries.isPending

  /**
   * An .xlsx template rather than a CSV, because the source file is a workbook
   * and a CSV round-trip is where the descriptions get mangled — they contain
   * commas ("NK CELLS, APHERESIS|…") and a spreadsheet that re-saves one
   * unquoted splits the column.
   *
   * The sheet is named `Parsed` to match the source workbook, which is also the
   * sheet the importer prefers when a file has several.
   */
  async function downloadTemplate() {
    setBuildingTemplate(true)
    try {
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Parsed')
      ws.addRow(CATALOG_IMPORT_HEADERS)
      ws.getRow(1).font = { bold: true }
      ws.addRow(TEMPLATE_EXAMPLE_ROW)
      const buf = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'isbt-product-codes-template.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setBuildingTemplate(false)
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setFile(null)
      setError(null)
      importEntries.reset()
      onClose()
    }
  }

  async function handleImport() {
    if (!file) return
    setParsing(true)
    setError(null)
    try {
      const isCsv = file.name.toLowerCase().endsWith('.csv')
      const { entries, errors } = isCsv
        ? parseProductCatalogCsv(await file.text())
        : await parseProductCatalogWorkbook(await file.arrayBuffer())
      if (errors.length > 0) {
        setError(errors.join(' '))
        return
      }
      if (entries.length === 0) {
        setError('No product rows found in the file.')
        return
      }
      importEntries.mutate(entries, { onSuccess: () => setFile(null) })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.')
    } finally {
      setParsing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import product codes</DialogTitle>
          <DialogDescription>
            Load a parsed ISBT 128 file. Matching on the product code, so re-importing the same
            file refreshes those rows rather than duplicating them.
          </DialogDescription>
        </DialogHeader>

        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          onDragOver={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDrop={e => {
            e.preventDefault()
            e.stopPropagation()
            const dropped = e.dataTransfer.files?.[0]
            if (dropped) setFile(dropped)
          }}
          className='flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-6 py-10 text-center transition-colors hover:bg-muted/40'
        >
          <UploadCloud className='h-8 w-8 text-muted-foreground' />
          <p className='text-sm font-medium'>{file?.name ?? 'Drag file here or click to upload'}</p>
          <p className='text-xs text-muted-foreground'>
            CSV or Excel (.csv, .xlsx, .xls). Product_Code is required; every other parsed column
            is optional and matched by its header name. A workbook with several sheets is read
            from the one named <span className='font-medium'>Parsed</span>.
          </p>
        </button>
        <input
          ref={inputRef}
          type='file'
          accept='.csv,.xlsx,.xls'
          className='hidden'
          onChange={e => setFile(e.target.files?.[0] ?? null)}
        />

        <button
          type='button'
          onClick={downloadTemplate}
          className='flex items-center gap-1.5 text-sm text-primary hover:underline'
        >
          <Download className='h-3.5 w-3.5' />
          {buildingTemplate ? 'Building template…' : 'Download Excel template'}
        </button>

        {error && <p className='text-sm text-destructive'>{error}</p>}
        {importEntries.error instanceof Error && (
          <p className='text-sm text-destructive'>{importEntries.error.message}</p>
        )}
        {importEntries.isPending && progress.total > 0 && (
          <div className='space-y-1'>
            <div className='h-1.5 w-full overflow-hidden rounded-full bg-muted'>
              <div
                className='h-full bg-primary transition-[width]'
                style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
              />
            </div>
            <p className='text-xs text-muted-foreground'>
              {progress.done.toLocaleString()} of {progress.total.toLocaleString()} rows sent…
            </p>
          </div>
        )}
        {result && (
          <p className='text-sm text-muted-foreground'>
            Imported {result.total.toLocaleString()} row
            {result.total === 1 ? '' : 's'}: {result.created.toLocaleString()} added,{' '}
            {result.updated.toLocaleString()} updated.
          </p>
        )}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' disabled={busy} onClick={() => handleOpenChange(false)}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          <Button disabled={!file || busy} onClick={handleImport}>
            {parsing ? 'Reading file…' : importEntries.isPending ? 'Importing…' : 'Import'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
